<template>
  <v-row no-gutters>
    <v-col cols="12">
      <div class="upload-section">
        <v-progress-circular
          v-if="showPreloader"
          indeterminate
          color="primary"
          size="32"
          class="ml-4"></v-progress-circular>
        <!-- .upload-section stacks vertically; the picker and the paste button share a row. -->
        <div class="d-flex align-center flex-wrap ga-2">
          <v-btn
            v-if="props.hideFileName && !props.pasteOnly"
            variant="outlined"
            color="primary"
            prepend-icon="mdi-paperclip"
            :disabled="props.disabled || showPreloader"
            @click="pdfInput?.click()">
            {{ props.multiple ? "Choose Files" : "Choose File" }}
          </v-btn>
          <input
            v-if="!props.pasteOnly"
            ref="pdfInput"
            :loading="showPreloader"
            type="file"
            :accept="props.accept"
            :multiple="props.multiple"
            :disabled="props.disabled"
            :class="{ 'hidden-input': showPreloader, 'native-input-hidden': props.hideFileName }"
            @change="handleFileUpload" />
          <v-btn
            v-if="props.allowPaste"
            variant="outlined"
            color="primary"
            prepend-icon="mdi-clipboard-outline"
            :loading="pasting"
            :disabled="props.disabled || showPreloader"
            @click="pasteFromClipboard">
            Paste Screenshot
          </v-btn>
        </div>
        <span v-if="awaitingPastePrompt" class="text-body-2 text-medium-emphasis mt-1">
          Waiting for the browser — click <strong>Paste</strong> in the prompt it just opened.
        </span>
        <span v-else-if="props.pasteShortcut" class="text-body-2 text-medium-emphasis mt-1">
          {{ pasteHint }}
        </span>
        <span v-if="progressText" class="text-body-2 text-medium-emphasis ml-2">{{ progressText }}</span>
        <v-alert v-if="uploadError" type="error" variant="tonal" density="compact" class="mt-2">
          {{ uploadError }}
        </v-alert>
        <!-- Problems with the paste itself (wrong type, nothing usable) — the upload never started,
             so this is separate from uploadError. -->
        <v-alert v-if="pasteAlert" :type="pasteAlert.type" variant="tonal" density="compact" class="mt-2">
          {{ pasteAlert.text }}
        </v-alert>
      </div>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref } from "vue";
import { uuid } from "vue-uuid";
import { usePastedFiles } from "@/composables/usePastedFiles";
import { uploadService, validateFile, parseUploadError } from "@/services/uploadService";

const props = defineProps({
  downloadDestination: {
    type: String,
    default() {
      return "";
    },
  },
  downloadLabel: {
    type: String,
    default() {
      return "";
    },
  },
  downloadDestinationIndex: {
    type: Number,
    default() {
      return null;
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // Replace the native file input with a plain "Choose File" button, so the browser's
  // selected-file name is not rendered next to it. For callers that list the uploaded
  // files themselves and would otherwise show the name twice.
  hideFileName: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // Accept a whole selection at once, emitting one downloadLink per file in the order picked.
  // Off by default: callers that map one upload to one slot (supporting documents) expect a
  // single file per pick.
  multiple: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // How many files this pick may contain, for callers with a cap of their own. The whole
  // selection is rejected before anything is uploaded — otherwise the overflow files would be
  // PUT to S3 and then dropped by the caller, leaving orphaned objects and no error.
  // null (default) means no limit.
  maxFiles: {
    type: Number,
    default() {
      return null;
    },
  },
  // Show a "Paste Screenshot" button that reads the image on the clipboard as if it had been
  // picked from disk. Images only — that is all the browser exposes to a script; the accept
  // filter still applies.
  allowPaste: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // Also take Cmd/Ctrl+V — a snipped screenshot, or a file copied out of the OS file manager.
  // See usePastedFiles: the listener is on the document, so enable this on one uploader per page.
  pasteShortcut: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // Render the paste button alone, with no file picker. For a caller that already has a picker
  // per slot and wants one paste entry point beside its list.
  pasteOnly: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // File types this uploader takes, in <input accept> form. Drives both the picker's filter and
  // what a paste is allowed to contribute; the default is everything the backend can store.
  accept: {
    type: String,
    default() {
      return ".tif, .tiff, .pdf, .png, .jpg, .jpeg";
    },
  },
});

const emit = defineEmits(["downloadLink", "uploadError"]);
const showPreloader = ref(false);
const pdfInput = ref(null);
const uploadError = ref(null);
// Only shown while a multi-file selection is in flight, so the user can see it progressing.
const progressText = ref("");

// Upload one file and announce it. Returns an error message, or null on success.
async function uploadOne(file) {
  const validationError = validateFile(file);
  if (validationError) return validationError;
  try {
    const { downloadUrl, uploadKey } = await uploadService.uploadFileWithKey(file, uuid.v4() + "/" + file.name);
    emit("downloadLink", {
      link: downloadUrl,
      storeVar: props.downloadDestination,
      label: props.downloadLabel,
      storeVarIndex: props.downloadDestinationIndex,
      fileName: file.name,
      uploadKey,
    });
    return null;
  } catch (error) {
    return parseUploadError(error);
  }
}

// Upload a whole selection (picked or pasted), reporting per-file failures together. Returns false
// if nothing landed or part of it failed, which is how usePastedFiles knows a paste can be retried.
const processFiles = async (files) => {
  if (files.length === 0) return;

  uploadError.value = null;
  // A picked file supersedes whatever the last paste complained about.
  clearPasteAlert();
  if (props.maxFiles !== null && files.length > props.maxFiles) {
    uploadError.value =
      props.maxFiles > 0
        ? `You selected ${files.length} files but only ${props.maxFiles} more can be added. Select fewer files and try again.`
        : "No more files can be added here. Remove one first.";
    emit("uploadError", uploadError.value);
    if (pdfInput.value) pdfInput.value.value = "";
    return false;
  }

  showPreloader.value = true;
  // Uploaded one at a time so the emitted order matches the selected order, and so a
  // multi-file pick doesn't fire a burst of presign + PUT round trips at once.
  const failures = [];
  for (const [index, file] of files.entries()) {
    progressText.value = files.length > 1 ? `Uploading ${index + 1} of ${files.length}…` : "";
    const failure = await uploadOne(file);
    if (failure) failures.push(files.length > 1 ? `${file.name}: ${failure}` : failure);
  }
  progressText.value = "";
  showPreloader.value = false;

  if (failures.length > 0) {
    uploadError.value = failures.join(" ");
    emit("uploadError", uploadError.value);
  }
  // Reset so re-picking the same file(s) fires @change again. Always safe when the native input
  // is hidden (the caller renders its own list); on a failure it also clears the stale selection.
  if ((props.hideFileName || failures.length > 0) && pdfInput.value) {
    pdfInput.value.value = "";
  }
  return failures.length === 0;
};

const handleFileUpload = async (event) => {
  await processFiles(Array.from(event.target.files || []));
};

// Declared after processFiles because the composable takes it as its callback; processFiles in
// turn only calls clearPasteAlert once the user acts, well after setup.
const { pasteFromClipboard, pasting, awaitingPastePrompt, pasteHint, pasteAlert, clearPasteAlert, forgetPaste } =
  usePastedFiles({
    onFiles: processFiles,
    enabled: () => (props.allowPaste || props.pasteShortcut) && !props.disabled && !showPreloader.value,
    multiple: () => props.multiple,
    accept: () => props.accept,
    listen: () => props.pasteShortcut,
  });

// Callers that let the user remove an uploaded file should call this with that file's name, so it
// can be pasted again instead of being turned away as a duplicate. Only that file is affected —
// every other pasted file stays deduplicated.
defineExpose({ forgetPaste });
</script>

<style lang="scss" scoped>
@import "../styles/components/_upload-file-component.scss";

.upload-loader {
  position: absolute;
  z-index: 1;
}

.hidden-input {
  opacity: 0;
  position: absolute;
  z-index: 0;
}

// Native input replaced by the "Choose File" button — kept in the DOM so it can be clicked
// programmatically, but never rendered (which is what hides the selected-file name).
.native-input-hidden {
  display: none;
}
</style>
